(() => {
  "use strict";

  const SELECTOR = "[data-elizna-reviews]";

  function text(value, fallback = "") {
    return value === null || value === undefined || value === ""
      ? fallback
      : String(value);
  }

  function number(value, fallback = 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function formatDate(value) {
    if (!value) return "";

    const raw = String(value).trim();
    const normalized =
      /[zZ]|[+-]\d\d:?\d\d$/.test(raw)
        ? raw
        : raw.replace(" ", "T") + "Z";

    const date = new Date(normalized);

    if (Number.isNaN(date.getTime())) return "";

    return new Intl.DateTimeFormat(document.documentElement.lang || "en", {
      year: "numeric",
      month: "short",
      day: "numeric"
    }).format(date);
  }

  function stars(rating) {
    const safe = Math.max(0, Math.min(5, Math.round(number(rating))));
    return "★".repeat(safe) + "☆".repeat(5 - safe);
  }

  function create(tag, className, value) {
    const element = document.createElement(tag);

    if (className) element.className = className;

    if (value !== undefined && value !== null) {
      element.textContent = String(value);
    }

    return element;
  }

  function appendMeta(container, label, value) {
    if (value === null || value === undefined || value === "") return;

    const row = create("div", "elizna-review-v2__meta-row");
    row.append(
      create("span", "elizna-review-v2__meta-label", label),
      create("span", "elizna-review-v2__meta-value", value)
    );

    container.append(row);
  }

  function renderReview(review) {
    const article = create("article", "elizna-review-v2");

    const identity = create("div", "elizna-review-v2__identity");

    identity.append(
      create(
        "p",
        "elizna-review-v2__name",
        text(review.display_name, "ELIZNA Customer")
      )
    );

    if (review.verified_buyer === true) {
      identity.append(
        create(
          "span",
          "elizna-review-v2__verified",
          "Verified Buyer"
        )
      );
    }

    const created = formatDate(review.created_at || review.published_at);
    if (created) {
      identity.append(
        create("span", "elizna-review-v2__date", created)
      );
    }

    const meta = create("div", "elizna-review-v2__meta");

    appendMeta(meta, "Lash concern", review.lash_concern);
    appendMeta(meta, "Time using ELIZNA™", review.usage_period);
    appendMeta(meta, "Experience noticed", review.experience_noticed);

    if (meta.children.length) {
      identity.append(meta);
    }

    const content = create("div", "elizna-review-v2__content");

    const rating = create(
      "div",
      "elizna-review-v2__rating",
      stars(review.rating)
    );

    rating.setAttribute(
      "aria-label",
      `${number(review.rating)} out of 5 stars`
    );

    content.append(rating);

    if (review.title) {
      content.append(
        create("h3", "elizna-review-v2__title", review.title)
      );
    }

    if (review.body) {
      content.append(
        create("p", "elizna-review-v2__body", review.body)
      );
    }

    if (review.recommend_product === true) {
      content.append(
        create(
          "p",
          "elizna-review-v2__recommend",
          "✓ I recommend this product"
        )
      );
    }

    article.append(identity, content);

    return article;
  }

  async function fetchJson(url) {
    const response = await fetch(url, {
      method: "GET",
      mode: "cors",
      cache: "no-store",
      credentials: "omit",
      headers: {
        Accept: "application/json"
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    return response.json();
  }

  function init(root) {
    if (!(root instanceof HTMLElement)) return;
    if (root.dataset.eliznaReviewsReady === "true") return;

    root.dataset.eliznaReviewsReady = "true";

    const apiBase =
      root.dataset.apiBase || "https://api.elizna.com";

    const handle =
      root.dataset.productHandle || "elizna-eyelash-serum";

    const loading = root.querySelector("[data-review-loading]");
    const empty = root.querySelector("[data-review-empty]");
    const error = root.querySelector("[data-review-error]");
    const list = root.querySelector("[data-review-list]");
    const more = root.querySelector("[data-review-more]");
    const average = root.querySelector("[data-review-average]");
    const count = root.querySelector("[data-review-count]");
    const countLabel = root.querySelector("[data-review-count-label]");
    const summaryStars = root.querySelector("[data-review-stars]");

    const state = {
      page: 1,
      limit: 5,
      loading: false,
      hasMore: false
    };

    function setSummary(summary) {
      const reviewCount = number(summary?.review_count);
      const avg = number(summary?.average_rating);

      if (average) average.textContent = avg.toFixed(1);
      if (count) count.textContent = String(reviewCount);

      if (countLabel) {
        countLabel.textContent =
          reviewCount === 1 ? "Review" : "Reviews";
      }

      if (summaryStars) {
        const percent = Math.max(
          0,
          Math.min(100, (avg / 5) * 100)
        );

        summaryStars.style.setProperty(
          "--rating-percent",
          `${percent}%`
        );

        summaryStars.setAttribute(
          "aria-label",
          `${avg.toFixed(1)} out of 5 stars`
        );
      }
    }

    async function loadSummary() {
      const ts = Date.now();

      const summary = await fetchJson(
        `${apiBase}/reviews/product/${encodeURIComponent(handle)}/summary?_=${ts}`
      );

      setSummary(summary);
    }

    async function loadPage(reset = false) {
      if (state.loading) return;

      state.loading = true;

      if (more) {
        more.disabled = true;
        more.textContent = "LOADING…";
      }

      try {
        if (reset) {
          state.page = 1;
          if (list) list.replaceChildren();
        }

        const ts = Date.now();

        const payload = await fetchJson(
          `${apiBase}/reviews/product/${encodeURIComponent(handle)}?page=${state.page}&limit=${state.limit}&_=${ts}`
        );

        const reviews = Array.isArray(payload?.reviews)
          ? payload.reviews
          : [];

        reviews.forEach((review) => {
          list?.append(renderReview(review));
        });

        state.hasMore = payload?.has_more === true;

        const totalVisible = list?.children.length || 0;

        if (loading) loading.hidden = true;
        if (error) error.hidden = true;

        if (totalVisible === 0) {
          if (empty) empty.hidden = false;
          if (list) list.hidden = true;
        } else {
          if (empty) empty.hidden = true;
          if (list) list.hidden = false;
        }

        if (more) {
          more.hidden = !state.hasMore;
        }
      } catch (err) {
        console.error("[ELIZNA Reviews]", err);

        if (loading) loading.hidden = true;
        if (empty) empty.hidden = true;
        if (list) list.hidden = true;
        if (more) more.hidden = true;
        if (error) error.hidden = false;
      } finally {
        state.loading = false;

        if (more) {
          more.disabled = false;
          more.textContent = "SHOW MORE";
        }
      }
    }

    more?.addEventListener("click", async () => {
      if (!state.hasMore || state.loading) return;

      state.page += 1;
      await loadPage(false);
    });

    Promise.allSettled([
      loadSummary(),
      loadPage(true)
    ]);
  }

  function initAll(scope = document) {
    scope.querySelectorAll?.(SELECTOR).forEach(init);
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => initAll(document),
      { once: true }
    );
  } else {
    initAll(document);
  }

  document.addEventListener("shopify:section:load", (event) => {
    initAll(event.target);
  });
})();